import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Bivariate data: scatter plots and two-way tables.
 *
 * The mathematics here is light and the reasoning is not. This is where a
 * student learns that data can show two things moving together without one
 * causing the other — which is the single most consequential statistical idea
 * most people will ever use, and the one most often got wrong in public.
 *
 * The two lessons are the same question asked of two kinds of data. A scatter
 * plot needs both variables to be *measured*; a two-way table works when they
 * can only be *counted* — left-handed or not, bus or bicycle — which is most of
 * the data anyone actually meets. The technique that replaces the line of best
 * fit is the relative frequency, and its whole difficulty is choosing the
 * denominator.
 */
export const SCATTER_LESSONS: Lesson[] = [
  lesson({
    id: 'scatter-plots-01',
    unitId: 'unit-bivariate-data',
    conceptId: 'scatter-plots',
    title: 'Scatter Plots and Association',
    summary: 'Looking for a relationship between two measurements — and refusing to claim one causes the other.',
    objectives: [
      'Plot and read a scatter plot of two measurements',
      'Describe an association as positive, negative or absent',
      'Use a line of best fit, and say what it cannot tell you',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Two measurements at once',
        blocks: [
          text('Every graph you have drawn so far showed one measurement. A scatter plot shows two, taken from the same thing — a person’s height *and* their arm span, a car’s age *and* its price.'),
          text('Each dot is one individual, placed at its pair of values. The question a scatter plot answers is whether the two measurements have anything to do with each other.'),
        ],
      },
      intuition: {
        title: 'Reading the cloud',
        blocks: [
          text('You are not looking at individual dots. You are looking at the shape of the whole cloud.'),
          table(['Pattern', 'Called', 'Means'], [
            ['dots rise left to right', 'positive association', 'as one grows, so does the other'],
            ['dots fall left to right', 'negative association', 'as one grows, the other shrinks'],
            ['no tilt at all', 'no association', 'knowing one tells you nothing'],
            ['tight to a line', 'strong', 'the pattern is reliable'],
            ['widely spread', 'weak', 'the pattern is only a tendency'],
          ]),
          text('When there is an association, a **line of best fit** summarises it — a single straight line drawn to pass as close as possible to the cloud as a whole. Its slope is then a rate, exactly as in the last unit: how much y changes per unit of x.'),
          insight('Now the part that matters more than any of the arithmetic. A scatter plot can show that two things move **together**. It cannot show that one **causes** the other.'),
          text('Ice-cream sales and sunburn cases rise together all summer. Ice cream does not cause sunburn, and sunburn does not sell ice cream. Hot weather causes both — a **confounding variable** sitting behind the pattern.'),
          warning('This error is everywhere in advertising, journalism and politics, and it is usually made by people quoting real data accurately. Association is evidence that something is going on. It is not evidence about *what*.', 'The error worth never making'),
          text('A second caution: a line of best fit is trustworthy only across the range of the data it was drawn from. Extending it far beyond that — **extrapolation** — assumes the pattern continues, which nothing in the data supports. A child’s growth chart extended to age forty predicts a giant.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'coordinate-grid',
              min: -1,
              max: 11,
              points: [
                { x: 1, y: 3 },
                { x: 2, y: 4 },
                { x: 3, y: 4 },
                { x: 4, y: 6 },
                { x: 5, y: 6 },
                { x: 6, y: 8 },
                { x: 7, y: 7 },
                { x: 8, y: 9 },
              ],
              lines: [{ slope: 0.8, intercept: 2.4, label: 'best fit', dashed: true }],
            },
            'A positive association. The dots do not sit on the line, and are not meant to — the line summarises the tendency, it does not claim to predict each individual.',
          ),
          text('Notice the point at (7, 7), below the line while its neighbours are above it. Real data always scatters, and a single point away from the trend is not an error to be removed.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A study finds that towns with more libraries have higher average incomes, with a line of best fit y = 1200x + 24000, where x is the number of libraries and y the average income in pounds.'),
          steps([
            { label: 'Predict the income for a town with 5 libraries.', latex: '1200(5) + 24000 = 30{,}000' },
            { label: 'Interpret the slope as a rate.', latex: '£1200 \\text{ per library, on average}' },
            { label: 'Interpret the intercept.', latex: '£24{,}000 \\text{ where } x = 0' },
            { label: 'Now the honest conclusion.', latex: '\\text{association, not causation}' },
          ]),
          text('Building a library will not raise anyone’s income by £1200. Wealthier towns can afford more libraries, and larger towns have both more libraries and different economies. The line describes the pattern; it does not explain it.'),
          tip('When you interpret a slope, say "associated with" rather than "causes" unless you know how the data was collected. It is one word, and it is the difference between a true statement and a false one.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('scatter-plots', 4)] },
      independent: { title: 'On your own', blocks: [practice('scatter-plots', 4)] },
      challenge: { title: 'Something harder', blocks: [practice('scatter-plots', 3, 'Deciding what a study does and does not show.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A scatter plot shows two measurements from each individual, one dot each.',
            'An association can be positive, negative or absent, and strong or weak.',
            'A line of best fit summarises the trend; its slope is a rate.',
            'Association is not causation — look for a variable behind both.',
            'A line of best fit is only trustworthy across the range of the data.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'two-way-tables-01',
    unitId: 'unit-bivariate-data',
    conceptId: 'two-way-tables',
    title: 'Two-Way Tables',
    summary: 'Association between two things you can count but not measure — and the row that has to be the denominator.',
    objectives: [
      'Read counts and totals from a two-way table',
      'Calculate a relative frequency within a row or a column',
      'Judge whether the table suggests an association between the two variables',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'When the data cannot be plotted',
        blocks: [
          text('A scatter plot needs two *measurements* — a height and an arm span, an age and a price. But much of the data anyone collects is not measured at all. It is sorted: left-handed or right-handed, walks or cycles, took the class or did not.'),
          text('You cannot plot "left-handed" on an axis. What you can do is count, and arrange the counts so that both questions are visible at once:'),
          table(['', 'Cycles', 'Does not cycle', 'Total'], [
            ['Year 8', '24', '36', '60'],
            ['Year 9', '15', '45', '60'],
            ['Total', '39', '81', '120'],
          ]),
          text('That is a **two-way table**. Every one of the 120 students is counted exactly once, in the one cell that matches both of their answers.'),
        ],
      },
      intuition: {
        title: 'The denominator is the whole question',
        blocks: [
          text('Look at the table again and ask: are Year 8 students more likely to cycle than Year 9 students?'),
          text('Comparing the raw counts, 24 against 15, only works here because the two year groups happen to be the same size. Change one group to 200 students and the comparison collapses. What makes the comparison honest is the **relative frequency** — the count as a fraction of the group you are asking about.'),
          math('\\text{Year 8: } \\frac{24}{60} = 40\\%, \\qquad \\text{Year 9: } \\frac{15}{60} = 25\\%'),
          insight('Notice which number went underneath. The question was about Year 8 students, so Year 8 is the whole — 60, not 120. Dividing by the grand total would answer a different question: what share of *everybody* is a cycling Year 8.'),
          warning('Both calculations are arithmetically fine and they mean completely different things. Before you divide, say the sentence out loud: "of the Year 8 students, how many cycle?" The group named after "of" is the denominator.', 'Which total?'),
          text('Once the rows are converted to percentages, the comparison is the point: 40% against 25% is a real gap, so the table suggests an **association** between year group and cycling. Had both rows come out near 33%, knowing someone’s year would tell you nothing about whether they cycle, and there would be no association to report.'),
          text('And the same discipline applies as it did to scatter plots. An association in a table says these two things go together. It does not say one caused the other, and the confounding variable is usually easy to find if you look — here, perhaps, how far each year group lives from school.'),
        ],
      },
      visual: {
        title: 'The same table, twice',
        blocks: [
          table(['Counts', 'Cycles', 'Does not cycle', 'Total'], [
            ['Year 8', '24', '36', '60'],
            ['Year 9', '15', '45', '60'],
          ]),
          table(['As a share of each row', 'Cycles', 'Does not cycle', 'Total'], [
            ['Year 8', '40%', '60%', '100%'],
            ['Year 9', '25%', '75%', '100%'],
          ]),
          tip('Every row of the second table adds to 100%, which is the check that you divided by the row total rather than by something else. If your rows do not total 100%, you used the wrong denominator.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Of 200 people surveyed, 80 own a dog. Of the dog owners, 60 walk daily; of the rest, 45 walk daily. Is dog ownership associated with walking daily?'),
          steps([
            { label: 'Fill in the table. The non-owners are 200 − 80.', latex: '120 \\text{ non-owners}' },
            { label: 'Relative frequency among owners.', latex: '\\frac{60}{80} = 75\\%' },
            { label: 'Relative frequency among non-owners.', latex: '\\frac{45}{120} = 37.5\\%' },
            { label: 'Compare the two shares, not the two counts.', latex: '75\\% > 37.5\\%' },
          ]),
          text('The counts alone would have said 60 against 45 — a small gap, and one that would still have appeared even if the two groups behaved identically, simply because there are more non-owners. The percentages tell the real story, and they differ by a lot.'),
          tip('Say what the data supports and no more: dog ownership and daily walking are associated. Whether dogs get people walking, or people who like walking get dogs, this table cannot tell you.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('two-way-tables', 4)] },
      independent: { title: 'On your own', blocks: [practice('two-way-tables', 4)] },
      challenge: { title: 'Something harder', blocks: [practice('two-way-tables', 3, 'Choosing the right total, and judging association from it.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A two-way table counts each individual once, in the cell matching both answers.',
            'Raw counts only compare fairly when the groups are the same size.',
            'A relative frequency needs the right denominator — the group the question is about.',
            'Rows converted to percentages should each total 100%.',
            'Different row percentages suggest an association; similar ones suggest none.',
            'An association in a table is still not causation.',
          ]),
        ],
      },
    },
  }),
];
